<template>
  <div>
    <v-row
      class="align-center py-3 px-2 precio-row"
      no-gutters
      :class="{ 'row-inactiva': !item.activo }"
    >
      <!-- En celular: identidad en una línea; debajo precios | badges y
           calidad | estado, de a dos por fila. -->
      <!-- ── ZONA 1: Identidad ── -->
      <v-col cols="12" md="3" class="d-flex align-center zona-dimable" style="gap: 4px">
        <v-checkbox
          :input-value="selected"
          dense
          hide-details
          class="mt-0 pt-0 flex-shrink-0"
          @change="$emit('toggle-select', item)"
        />
        <v-img
          :src="item.imagen || noImage"
          width="48"
          height="48"
          max-width="48"
          max-height="48"
          class="rounded flex-shrink-0"
          contain
        ></v-img>
        <div class="overflow-hidden">
          <div
            class="body-2 font-weight-bold clickable-link text-wrap nombre"
            @click="verDetalle"
          >
            {{ item.descripcion }}
          </div>
          <div class="caption grey--text mt-1 text-truncate">
            {{ item.categoria ? item.categoria.name : "Sin categoría" }}
          </div>
          <div class="d-flex align-center mt-1" style="gap: 4px">
            <span v-if="item.controla_stock" class="caption">
              Stock: {{ item.cantidad_stock }}u.
            </span>
            <span v-else class="caption">Sin control</span>
          </div>
          <div v-if="item.presentacion_codigo_barras" class="caption grey--text text-truncate mt-1" style="font-size: 11px">
            <v-icon x-small style="vertical-align: middle">mdi-barcode</v-icon>
            {{ item.presentacion_codigo_barras }}
          </div>
        </div>
      </v-col>

      <!-- ── ZONA 2: Bloque Económico ── -->
      <v-col cols="6" md="2" class="zona-dimable mt-3 mt-md-0 zona-movil">
        <div class="d-flex align-start flex-column" style="gap: 2px">
          <div class="text-subtitle-1 font-weight-bold primary--text">
            {{ item.precio_venta | convertPesoArg }}
          </div>
          <div class="d-flex align-center" style="gap: 12px">
            <div>
              <div class="caption grey--text">Costo</div>
              <div class="body-2">{{ item.precio_costo | convertPesoArg }}</div>
            </div>
            <div>
              <div class="caption grey--text">Ganancia</div>
              <div
                class="body-2 font-weight-medium"
                :class="item.ganancia >= 0 ? 'success--text' : 'error--text'"
              >
                {{ item.ganancia }}%
              </div>
            </div>
          </div>
          <div class="caption grey--text">
            Sin IVA: {{ item.precio_base | convertPesoArg }}
          </div>
        </div>
      </v-col>

      <!-- ── ZONA 3: Badges ── -->
      <v-col cols="6" md="2" class="zona-dimable mt-3 mt-md-0">
        <div class="d-flex flex-wrap align-center" style="gap: 4px">
          <v-chip
            v-if="item.tiene_contabilidad"
            x-small
            outlined
            color="blue-grey"
          >
            <v-icon x-small left>mdi-book-outline</v-icon>
            Contab.
          </v-chip>
          <v-chip v-if="item.controla_stock" x-small outlined color="teal">
            <v-icon x-small left>mdi-warehouse</v-icon>
            Stock
          </v-chip>
        </div>
      </v-col>

      <!-- ── ZONA 5: Calidad ── -->
      <v-col cols="6" md="2" class="d-flex align-center justify-start justify-md-center zona-dimable mt-2 mt-md-0 zona-movil">
        <v-tooltip
          bottom
          :max-width="220"
          :disabled="indicadoresFaltantes.length === 0"
        >
          <template v-slot:activator="{ on }">
            <div
              class="d-flex flex-column align-center"
              style="cursor: default"
              v-on="on"
            >
              <!-- Semicírculo SVG -->
              <svg :width="84" :height="50" viewBox="0 0 100 58">
                <!-- Fondo -->
                <path
                  d="M 10,50 A 40,40 0 0,1 90,50"
                  fill="none"
                  stroke="rgba(255,255,255,0.12)"
                  stroke-width="4"
                  stroke-linecap="round"
                />
                <!-- Progreso -->
                <path
                  d="M 10,50 A 40,40 0 0,1 90,50"
                  fill="none"
                  :stroke="svgColor"
                  stroke-width="4"
                  stroke-linecap="round"
                  :stroke-dasharray="`${
                    (porcentajeCalidad / 100) * 125.66
                  } 125.66`"
                />
                <!-- Número -->
                <text
                  x="50"
                  y="48"
                  text-anchor="middle"
                  font-size="15"
                  font-weight="700"
                  :fill="svgColor"
                >
                  {{ porcentajeCalidad }}%
                </text>
              </svg>
            </div>
          </template>
          <!-- Tooltip: solo faltantes -->
          <div class="pa-1">
            <div
              class="caption font-weight-bold mb-2"
              style="letter-spacing: 0.05em"
            >
              Mejorar calidad
            </div>
            <div
              v-for="ind in indicadoresFaltantes"
              :key="ind.label"
              class="d-flex align-start mb-2"
              style="gap: 8px"
            >
              <v-icon
                x-small
                color="orange lighten-1"
                style="margin-top: 2px; flex-shrink: 0"
              >
                mdi-alert-circle-outline
              </v-icon>
              <div>
                <div class="caption font-weight-bold">{{ ind.label }}</div>
                <div class="caption grey--text">{{ ind.ayuda }}</div>
              </div>
            </div>
          </div>
        </v-tooltip>
      </v-col>

      <!-- ── ZONA 6: Switch + Estado y recomendaciones ── -->
      <v-col cols="6" md="3" class="d-flex align-center justify-end mt-2 mt-md-0" style="gap: 10px">
        <!-- Estado y recomendaciones -->
        <div class="d-flex flex-column align-end" style="gap: 4px; flex: 1">
          <v-chip
            x-small
            :color="item.activo ? 'success' : 'grey darken-2'"
            dark
          >
            {{ item.activo ? "Activo" : "Pausada" }}
          </v-chip>
          <div
            v-if="item.activo"
            class="caption grey--text text-right"
            style="font-size: 10px; max-width: 130px; line-height: 1.4"
          >
            {{ fechaRelativa }}
          </div>
          <div
            v-else
            class="caption orange--text text--lighten-1 text-right"
            style="font-size: 10px; max-width: 130px; line-height: 1.4"
          >
            No aparece en ventas ni en el listado de precios
          </div>
        </div>

        <!-- Switch -->
        <v-switch
          :input-value="item.activo"
          dense
          hide-details
          class="mt-0 pt-0 flex-shrink-0"
          @change="toggleActivo"
        ></v-switch>

        <!-- Menú 3 puntos -->
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon x-small v-bind="attrs" v-on="on">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="verDetalle">

              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-currency-usd</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Editar precio</v-list-item-title>
            </v-list-item>
            <v-list-item @click="verDetalle">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-open-in-new</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Ver detalle</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>
    <v-divider></v-divider>
  </div>
</template>

<script>
import noImage from "@/assets/no-image.png";

export default {
  name: "PrecioListItem",
  filters: {
    convertPesoArg(value) {
      if (value == null) return "—";
      return `$ ${new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)}`;
    },
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    uuidSucursal: {
      type: String,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { noImage };
  },
  computed: {
    indicadores() {
      return [
        {
          label: "Imagen",
          ok: !!this.item.imagen,
          ayuda: "Agregá una foto para generar más confianza en el comprador",
        },
        {
          label: "Proveedor",
          ok: !!this.item.proveedor,
          ayuda: "Asociá un proveedor para organizar tus compras",
        },
        {
          label: "Marca",
          ok: !!this.item.marca,
          ayuda: "Asigná una marca para una mejor clasificación",
        },
        {
          label: "Contabilidad",
          ok: !!this.item.tiene_contabilidad,
          ayuda: "Configurá los rubros contables de compra y venta",
        },
        {
          label: "Precio de venta",
          ok: (this.item.precio_venta || 0) > 0,
          ayuda: "Este producto no tiene precio de venta configurado",
        },
        {
          label: "Categoría",
          ok: !!this.item.categoria,
          ayuda: "Clasificá el producto en una categoría",
        },
      ];
    },
    indicadoresFaltantes() {
      return this.indicadores.filter((i) => !i.ok);
    },
    puntajeCalidad() {
      return this.indicadores.filter((i) => i.ok).length;
    },
    porcentajeCalidad() {
      return Math.round((this.puntajeCalidad / this.indicadores.length) * 100);
    },
    svgColor() {
      if (this.porcentajeCalidad >= 80) return "#66bb6a";
      if (this.porcentajeCalidad >= 50) return "#ffa726";
      return "#ef5350";
    },
    fechaRelativa() {
      if (!this.item.fecha_actualizacion) return "Sin actualizaciones";
      const diff =
        Date.now() - new Date(this.item.fecha_actualizacion).getTime();
      const dias = Math.floor(diff / 86400000);
      if (dias === 0) return "Actualizado hoy";
      if (dias === 1) return "Actualizado ayer";
      return `Actualizado hace ${dias} días`;
    },
  },
  methods: {
    verDetalle() {
      this.$router.push({
        name: "empresa.sucursal.productos.item",
        params: {
          uuid_sucursal: this.uuidSucursal,
          id_producto_sucursal: this.item.id,
        },
      });
    },
    toggleActivo(val) {
      this.$emit("toggle-activo", { item: this.item, activo: val });
    },
  },
};
</script>

<style scoped>
.precio-row {
  transition: background 0.15s;
}
.precio-row:hover {
  background: rgba(255, 255, 255, 0.03);
}
.clickable-link {
  cursor: pointer;
}
@media (min-width: 960px) {
  .nombre {
    max-width: 200px;
  }
}
@media (max-width: 959px) {
  /* Alinear con el nombre (checkbox + imagen) */
  .zona-movil {
    padding-left: 32px;
  }
}
.clickable-link:hover {
  text-decoration: underline;
}
.row-inactiva .zona-dimable {
  opacity: 0.35;
  pointer-events: none;
}
</style>
